import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const getRoleApiError = (error) => {
  const data = error.response?.data;
  if (data?.errors && typeof data.errors === "object") {
    const messages = Object.values(data.errors)
      .flat()
      .filter((item) => typeof item === "string" && item.trim());
    if (messages.length) return messages.join(" — ");
  }
  return data?.message || "An unknown error occurred";
};

// all roles
export const fetchRoles = createAsyncThunk("roles/fetchAll", async (_,{rejectWithValue}) => {
  try {
    const response = await axios.get(`${BASE_URL}/roles`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
        Lang: i18n.language,
      },
    });
    return response.data.data;
  } catch (error) {
    const message = error.response?.data?.message || "Failed load data";
    return rejectWithValue(message);
  }
});

// add roles
export const addRole = createAsyncThunk(
  "roles/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/roles/store`, userData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getRoleApiError(error));
    }
  }
);
// get role record
export const roleRecord = createAsyncThunk(
  "roles/roleRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/roles/show/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);
// update role
export const updateRole = createAsyncThunk(
  "roles/updateRole",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/roles/update`,
        { role_id: id, ...formData },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getRoleApiError(error));
    }
  }
);
// delete role
export const deleteRole = createAsyncThunk(
  "roles/deleteRole",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/roles/delete`,
        { role_id: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete role";
      return rejectWithValue({ message });
    }
  }
);
const RoleSlice = createSlice({
  name: "roles",
  initialState: {
    roles: [],
    record: null,
    isLoading: false,
    error: null,
    success: null,
    successDelete: null,
    errorDelete: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      .addCase(addRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(roleRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(roleRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload.data;
      })
      .addCase(roleRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to add data";
      })
      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          "Failed to update record";
      })
      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isLoading = false;

        // Remove from data array inside roles
        if (Array.isArray(state.roles?.data)) {
          state.roles.data = state.roles.data.filter(
            (record) => record.id !== action.meta.arg
          );
        } else if (Array.isArray(state.roles)) {
          state.roles = state.roles.filter(
            (record) => record.id !== action.meta.arg
          );
        }

        state.successDelete = action.payload?.message || "Deleted successfully";
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.errorDelete =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "Failed to delete role";
      });
  },
});

export const { clearState } = RoleSlice.actions;
export default RoleSlice.reducer;
